import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';

const defaultValues = {
  title: '',
  subject: '',
  educatorId: '',
  studentIds: [],
  startTime: '',
  endTime: '',
  notes: '',
};

const SessionFormDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
  educatorOptions,
  studentOptions,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (open) {
      reset({
        ...defaultValues,
        ...initialValues,
        startTime: initialValues?.startTime ? initialValues.startTime.slice(0, 16) : '',
        endTime: initialValues?.endTime ? initialValues.endTime.slice(0, 16) : '',
        studentIds: initialValues?.studentIds || [],
      });
    }
  }, [initialValues, open, reset]);

  const submitHandler = (values) => {
    onSubmit({
      ...values,
 startTime: values.startTime ? new Date(values.startTime).toISOString() : null,
 endTime: values.endTime ? new Date(values.endTime).toISOString() : null,
    });
  };

  const selectedStudents = watch('studentIds');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialValues?._id ? 'Edit Session' : 'Create Session'}</DialogTitle>
      <form onSubmit={handleSubmit(submitHandler)} noValidate>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Title"
              fullWidth
              error={Boolean(errors.title)}
              helperText={errors.title?.message}
              {...register('title', { required: 'Title is required' })}
            />
            <TextField
              label="Subject"
              fullWidth
              {...register('subject')}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Educator"
                fullWidth
                error={Boolean(errors.educatorId)}
                helperText={errors.educatorId?.message}
                {...register('educatorId', { required: 'Educator is required' })}
              >
                {educatorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Start time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.startTime)}
                helperText={errors.startTime?.message}
                {...register('startTime', { required: 'Start time is required' })}
              />
              <TextField
                label="End time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.endTime)}
                helperText={errors.endTime?.message}
                {...register('endTime', { required: 'End time is required' })}
              />
            </Stack>
            <TextField
              select
              label="Students"
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const match = studentOptions.find((option) => option.value === value);
                      return <Chip key={value} label={match?.label || value} size="small" />;
                    })}
                  </Box>
                ),
              }}
              error={Boolean(errors.studentIds)}
              helperText={errors.studentIds?.message}
              value={selectedStudents}
              onChange={(event) => setValue('studentIds', event.target.value, { shouldValidate: true })}
            >
              {studentOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={3}
              {...register('notes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
 {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SessionFormDialog;